import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  debounce,
  Divider,
  Grid2 as Grid,
  IconButton,
  InputBase,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useServices } from '@/components/ServiceContext.tsx';
import { GraphQLOptionsV6 } from '@aws-amplify/api-graphql';
import * as APITypes from '@/API.ts';
import { SearchableSortDirection } from '@/API.ts';
import { Resident } from '@/models/Resident.ts';
import { OptOutPageState } from '@/pages/BoughtListOptOutPage/BoughtListOptOutPage.tsx';
import log from 'loglevel';
import {
  buildSearchGraphQLQuery,
  sanitizeSearch,
} from '@/components/ProspectSearch/searchUtils.ts';
import theme from '@/theme.ts';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import { styled } from '@mui/system';

interface Props {
  id?: string;
  community?: string;
  size?: 'medium' | 'small';
}

const RESULT_LIMIT = 50;

const StyledListbox = styled('div')(() => ({
  boxSizing: 'border-box',
  '& ul': {
    padding: 0,
    margin: 0,
  },
}));

const ProspectSearch: React.FC<Props> = ({ size }) => {
  const { boughtListService, routeService } = useServices();
  const isSmall = size === 'small';

  const [modalOpen, setModalOpen] = useState(false);
  const [residentMatches, setResidentMatches] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const navigate = useNavigate();
  const isFetching = useRef<boolean>(false);

  const handleModalClose = () => setModalOpen(false);

  const navigateToOptOut = useCallback(
    async (selectedResidentId?: string) => {
      const query = selectedResidentId
        ? { id: { eq: selectedResidentId } }
        : buildSearchGraphQLQuery<APITypes.SearchableBoughtListFilterInput>(
            searchText,
            ['firstName', 'lastName', 'address']
          );
      navigate(
        routeService.getRoute('bought-list-opt-out-search')?.path || '/',
        {
          state: {
            query,
          } as OptOutPageState<APITypes.SearchableBoughtListFilterInput>,
          viewTransition: true,
        }
      );
    },
    [navigate, routeService, searchText]
  );

  const fetchResidents = useCallback(
    async (
      searchString: string,
      token?: string
    ): Promise<{ residents: Resident[]; nextToken?: string }> => {
      try {
        const queryVariables: GraphQLOptionsV6['variables'] = {
          limit: RESULT_LIMIT,
          nextToken: token,
          filter:
            buildSearchGraphQLQuery<APITypes.SearchableBoughtListFilterInput>(
              searchString,
              ['firstName', 'lastName', 'address']
            ),
          sort: [
            { field: 'lastName', direction: SearchableSortDirection.asc },
            { field: 'firstName', direction: SearchableSortDirection.asc },
          ],
        };
        const result = await boughtListService.searchProspects(queryVariables);
        const residents = result.items.map(Resident.deserialize);
        return { residents, nextToken: result.nextToken || undefined };
      } catch (error) {
        log.error('Error fetching residents:', error);
        return { residents: [], nextToken: undefined };
      }
    },
    [boughtListService]
  );

  const handleSearchChange = useCallback(
    async (input: string, reset: boolean = false) => {
      const sanitized = sanitizeSearch(input);
      if (sanitized.length > 2) {
        setLoading(true);
        isFetching.current = true;
        try {
          const { residents, nextToken } = await fetchResidents(sanitized);
          setResidentMatches(prev =>
            reset ? residents : [...prev, ...residents]
          );
          setNextToken(nextToken);
          setHasMore(Boolean(nextToken));
        } catch (error) {
          log.error('Search error:', error);
        } finally {
          setLoading(false);
          isFetching.current = false;
        }
      } else {
        setResidentMatches([]);
        setNextToken(undefined);
        setHasMore(false);
      }
    },
    [fetchResidents]
  );

  const debouncedSearch = useMemo(
    () => debounce((value: string) => handleSearchChange(value, true), 500),
    [handleSearchChange]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);
    void debouncedSearch(value);
  };

  // Function to load more data
  const loadMore = useCallback(() => {
    if (isFetching.current || !hasMore) return;
    setLoading(true);
    isFetching.current = true;
    fetchResidents(searchText, nextToken)
      .then(({ residents, nextToken: newToken }) => {
        setResidentMatches(prev => [...prev, ...residents]);
        setNextToken(newToken);
        setHasMore(Boolean(newToken));
      })
      .catch(error => {
        log.error('Error loading more residents:', error);
      })
      .finally(() => {
        setLoading(false);
        isFetching.current = false;
      });
  }, [fetchResidents, hasMore, nextToken, searchText]);

  const ListboxComponent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLElement>
  >(function ListboxComponent(props, ref) {
    const { children, ...other } = props;

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const listbox = event.currentTarget;
      if (
        listbox.scrollTop + listbox.clientHeight >=
        listbox.scrollHeight - 100
      ) {
        loadMore();
      }
    };

    return (
      <StyledListbox {...other} ref={ref} onScroll={handleScroll}>
        {children}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </StyledListbox>
    );
  });

  return (
    <>
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="search-modal-title"
        aria-describedby="search-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: '2px solid #000',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="search-modal-title" variant="h6" component="h2">
            The search feature is under development.
          </Typography>
        </Box>
      </Modal>

      <Paper
        component="form"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: isSmall ? '30%' : '45%',
          mt: isSmall ? '5px' : '50px',
          mb: '5px',
          boxShadow: 3,
        }}
      >
        <IconButton disabled sx={{ p: isSmall ? '5px' : '10px' }}>
          <AccountCircleOutlinedIcon fontSize={isSmall ? 'small' : 'medium'} />
        </IconButton>

        <Autocomplete
          sx={{ ml: 0.6, flex: 2, width: '100%', mr: 0.6 }}
          freeSolo
          disableClearable
          options={residentMatches.map(r => r.listId)}
          filterSelectedOptions
          filterOptions={x => x}
          onChange={(e, residentId) => {
            if (e.type === 'keydown') {
              void navigateToOptOut();
            } else {
              void navigateToOptOut(residentId);
            }
          }}
          slotProps={{
            listbox: { component: ListboxComponent },
          }}
          renderOption={(props, _, { index }) => {
            const { key, ...optionProps } = props;
            const resident = residentMatches[index];
            if (!resident) return null;

            return (
              <Box
                key={key}
                sx={{
                  borderRadius: '8px',
                  margin: '5px 0',
                  display: 'flex',
                  alignItems: 'center',
                }}
                component="li"
                {...optionProps}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Name Section */}
                    <Grid size={{ xs: 6 }} display="flex" alignItems="center">
                      <PersonIcon
                        fontSize="small"
                        sx={{ color: theme.palette.grey[600], mr: 1 }}
                      />
                      <Typography variant="body1" data-testid="resident-name">
                        {resident.lastName}, {resident.firstName}
                      </Typography>
                    </Grid>

                    {/* Address Section */}
                    <Grid size={{ xs: 6 }} display="flex" alignItems="center">
                      <HomeIcon
                        fontSize="small"
                        sx={{ color: theme.palette.grey[600], mr: 1 }}
                      />
                      <Typography
                        variant="body1"
                        data-testid="resident-address"
                      >
                        {resident.address}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            );
          }}
          renderInput={params => (
            <InputBase
              {...params.InputProps}
              fullWidth
              placeholder="Search by first or last name or address"
              onChange={handleInputChange}
              value={searchText}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              endAdornment={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={isSmall ? 16 : 20}
                      sx={{ mr: isSmall ? 1.875 : 1.5625 }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </Box>
              }
              inputProps={{
                ...params.inputProps,
                style: { fontSize: isSmall ? '0.7rem' : 'inherit' },
              }}
            />
          )}
        />

        <Divider sx={{ height: '100%' }} orientation="vertical" />
        <Divider sx={{ height: '100%' }} orientation="vertical" />

        <IconButton
          onClick={() => navigateToOptOut()}
          sx={{
            p: isSmall ? '5px' : '10px',
            ml: isSmall ? '3px' : '5px',
            mr: isSmall ? '3px' : '5px',
            color: theme.palette.primary.main,
          }}
          aria-label="search"
        >
          <PageviewIcon fontSize="large" />
        </IconButton>
      </Paper>
    </>
  );
};

export default ProspectSearch;
